import MenuTop from "../../../components/menu/top/menu-top";
import { useParams } from "react-router-dom";
import { deleteProjectService, finishedProjectService } from "../../../features/project/service/project-service";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import './style.css';
import { projectData } from "../../../features/project/forms/project-data";
import { useAlert } from "../../../components/alert/alert";
import { SEVERITY } from "../../../shared/interfaces/alert";
import { FormProps, FormTypes } from "../../../shared/interfaces/form";
import Form from "../../../components/form/form";
import FinanceTimeline from "../../../components/timeline/timeline";
import { CustomModal, useModal } from "../../../components/modals/common/index";
import { Typography } from "@mui/material";
import {
    fetchCachedCompaniesService,
    fetchCachedFinanciersService,
    fetchCachedProjectFinancialTimelineService,
    fetchCachedProjectService,
    fetchCachedSearchersListService,
    fetchCachedUserCompanyService,
    invalidateProjectDetailCache,
} from "../../../features/project/service/project-query-cache";

function ProjectList() {
    const { id } = useParams();
    const [ formData, setFormData ] = useState<FormProps[] | any>();
    const [ formSections, setFormSections ] = useState<any[]>();
    const { showAlert } = useAlert();
    const [ timelineData, setTimelineData ] = useState<any[]>();
    const [ isLoading, setIsLoading ] = useState(true);
    const [ projectDetails, setProjectDetails ] = useState<any>(null);
    const navigate = useNavigate();
    
    // Hooks para controlar o estado das modais
    const deleteModal = useModal();
    const finishModal = useModal();

    const fetchSearchers = useCallback(async () => {
        try {
            const response = await fetchCachedSearchersListService();
            return response?.map(({ name, id}: any) => ({ label: name, value: id }));
        } catch (error) {
            console.error("Error fetching searchers:", error);
            return [];
        }
    }, []);

    const fetchFinanciers = useCallback(async () => {
        try {
            const response = await fetchCachedFinanciersService();
            return response?.map(({ name, id}: any) => ({ label: name, value: id }));
        } catch (error) {
            console.error("Error fetching financiers:", error);
            return [];
        }
    }, []);

    const fetchListCompanies = useCallback(async () => {
        try {
            const response = await fetchCachedCompaniesService();
            return response?.map(({ name, id}: any) => ({ label: name, value: id }));
        } catch (error) {
            console.error("Error fetching companies:", error);
            return [];
        }
    }, []);

    const fetchUserCompany = useCallback(async () => {
        try {
            return await fetchCachedUserCompanyService();
        } catch (error) {
            console.error("Error fetching user company:", error);
            return null;
        }
    }, []);

    // Função para organizar os dados em seções
    // Função para organizar os dados em seções (CORRIGIDA para ficar igual ao edit)
    const organizeFormDataIntoSections = (data: FormProps[]) => {
        const projectFields = ['name', 'principalInvestigatorId', 'initialDate', 'finalDate', 'duration'];
        const financialFields = ['financierId', 'accountabilittyDate', 'valor', 'movements', 'balance', 'multiplePayments', 'multiplePaymentsRecurrency'];
        const clientFields = ['companyId'];

        const projectData = data.filter(field => projectFields.includes(field.name));
        const financialData = data.filter(field => financialFields.includes(field.name));
        const clientData = data.filter(field => clientFields.includes(field.name));

        return [
            {
                title: "Informações do Projeto",
                icon: "📋",
                color: "project",
                fields: projectData
            },
            {
                title: "Informações Financeiras", 
                icon: "💰",
                color: "financial",
                fields: financialData
            },
            {
                title: "Informações do Cliente",
                icon: "🏢", 
                color: "client",
                fields: clientData
            }
        ];
    };

    const fetchAllData = useCallback(async (projectId: string) => {
        try {
            // fetchProjectService agora retorna movements e balance calculados
            const response = await fetchCachedProjectService(projectId);
            const { principalInvestigator, financier, initialDate, finalDate, companyId, ...restResponse } = response;

            setProjectDetails(response);
            
            const [searchers, financiers, companies, userCompany, financialTimeline] = await Promise.all([
                fetchSearchers(),
                fetchFinanciers(),
                fetchListCompanies(),
                fetchUserCompany(),
                fetchCachedProjectFinancialTimelineService(projectId)
            ]);

            setTimelineData(financialTimeline);

            // Verificar se existe multiplePaymentsRecurrency e definir multiplePayments
            const hasMultiplePaymentsRecurrency = response.multiplePaymentsRecurrency != null && response.multiplePaymentsRecurrency !== '';
            if (hasMultiplePaymentsRecurrency) {
                restResponse.multiplePayments = true;
            }

            // ATUALIZADO: Agora passamos o timelineData para calcular movimentações e saldo
            const data = projectData(
                projectId, 
                restResponse,
                principalInvestigator || {}, 
                searchers || [], 
                financiers || [], 
                initialDate, 
                finalDate,
                financier,
                companyId,
                companies,
                userCompany?.name,
                financialTimeline, // <- Parâmetro timelineData adicionado
                undefined
            );

            // Se tem multiplePaymentsRecurrency, adicionar o campo após o checkbox (sempre desabilitado na visualização)
            if (hasMultiplePaymentsRecurrency) {
                const checkboxIndex = data.findIndex(item => item.name === 'multiplePayments');
                if (checkboxIndex !== -1) {
                    const newField = {
                        typeOfField: FormTypes.INPUT,
                        type: 'number',
                        placeholder: 'Recorrência dos pagamentos (em meses)',
                        value: response.multiplePaymentsRecurrency,
                        name: 'multiplePaymentsRecurrency',
                        width: 200,
                        height: 55,
                        disabled: true // Sempre desabilitado na visualização
                    };
                    
                    data.splice(checkboxIndex + 1, 0, newField);
                }
            }

            setFormData(data);
            
            // Organizar dados em seções
            const sections = organizeFormDataIntoSections(data);
            setFormSections(sections);

        } catch (error) {
            console.error("Error fetching project data:", error);
            showAlert('Erro ao carregar dados do projeto', SEVERITY.ERROR);
        } finally {
            setIsLoading(false);
        }
    }, [fetchSearchers, fetchFinanciers, fetchListCompanies, fetchUserCompany, showAlert]);

    // Função para lidar com a exclusão do projeto
    const handleDeleteProject = async () => {
        if (id) {
            try {
                setIsLoading(true);
                await deleteProjectService(id);
                invalidateProjectDetailCache(id);
                showAlert('Projeto excluído com sucesso', SEVERITY.SUCCESS);
                navigate('/dashboard', { replace: true });
            } catch (error) {
                console.error('Erro ao excluir projeto:', error);
                showAlert('Erro ao excluir projeto', SEVERITY.ERROR);
            } finally {
                setIsLoading(false);
                deleteModal.closeModal();
            }
        }
    };
    
    // Função para concluir o projeto
    const handleFinishProject = async () => {
        if (id) {
            try {
                setIsLoading(true);
                await finishedProjectService(id);
                invalidateProjectDetailCache(id);
                showAlert('Projeto concluído com sucesso', SEVERITY.SUCCESS);
                // Recarregar os dados do projeto após concluí-lo
                await fetchAllData(id);
            } catch (error) {
                console.error('Erro ao concluir projeto:', error);
                showAlert('Erro ao concluir projeto', SEVERITY.ERROR);
            } finally {
                setIsLoading(false);
                finishModal.closeModal();
            }
        }
    };

    useEffect(() => {
        if (id) { fetchAllData(id) }
    }, [id, fetchAllData]);

    const isProjectFinished = projectDetails?.isFinished;

    return (
        <div className="container-show-project">
            <MenuTop />
            <div className="container-show-project__container">
                <Form 
                    data={formData} 
                    sections={formSections}
                    title={`Visualizar projeto ${isProjectFinished ? '(Concluído)' : ''}`} 
                    onCancel={() => navigate('/dashboard', {replace: true})} 
                    isLoading={isLoading}
                    isReadOnly={true}
                    showEdit={!isProjectFinished}
                    showFinancial={!isProjectFinished}
                    showDeleteIcon={!isProjectFinished}
                    showFinished={!isProjectFinished}
                    onClickedMenuContextType={(type: string) => {
                        if (type === 'edit') {
                            navigate(`/projeto/${id}`, {replace: true});
                        }

                        if (type === 'financial') {
                            navigate(`/financeiro/${id}/adicionar-projeto`, {replace: true});
                        }
                        
                        if (type === 'delete') {
                            deleteModal.openModal();
                        }
                        
                        if (type === 'finished') {
                            finishModal.openModal();
                        }
                    }}
                />
                <FinanceTimeline data={timelineData as []}/>
                
                {/* Modal de confirmação de exclusão */}
                <CustomModal
                    open={deleteModal.isOpen}
                    onClose={deleteModal.closeModal}
                    title="Confirmar exclusão"
                    content={
                        <Typography>
                            Tem certeza que deseja excluir este projeto? Esta ação também excluirá todos os 
                            registros financeiros associados e não poderá ser desfeita.
                        </Typography>
                    }
                    buttons={[
                        {
                            id: 'cancel',
                            label: 'Cancelar',
                            variant: 'outlined',
                            color: 'primary',
                            onClick: deleteModal.closeModal
                        },
                        {
                            id: 'confirm',
                            label: 'Excluir',
                            variant: 'contained',
                            color: 'error',
                            onClick: handleDeleteProject
                        }
                    ]}
                />
                
                {/* Modal de confirmação para concluir projeto */}
                <CustomModal
                    open={finishModal.isOpen}
                    onClose={finishModal.closeModal}
                    title="Concluir projeto"
                    content={
                        <Typography>
                            Tem certeza que deseja marcar este projeto como concluído? 
                            Após a conclusão, não será possível adicionar novos registros financeiros 
                            ou editar as informações do projeto.
                        </Typography>
                    }
                    buttons={[
                        {
                            id: 'cancel',
                            label: 'Cancelar',
                            variant: 'outlined',
                            color: 'primary',
                            onClick: finishModal.closeModal
                        },
                        {
                            id: 'confirm',
                            label: 'Concluir projeto',
                            variant: 'contained',
                            color: 'success',
                            onClick: handleFinishProject
                        }
                    ]}
                />
            </div>
        </div>
    );
}

export default ProjectList;
